<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import { useDefaultToast } from "@/composables/useDefaultToast";
import { ModalName } from '@/constants/modals';
// Helpers
import { getStartMissionDate } from "@/helpers/date";
// Composables
import { useMissionActionHandlers } from "@/modules/companyNeed/composables/useMissionActionHandlers";
import { missionStatusInfoMap } from "@/modules/companyNeed/helpers/constants";
import { toManageOrderMissionForm } from "@/modules/companyNeed/helpers/manageOrderModal";
import {
    applyUpdatePayloadToMissionDetails,
    fetchMissionWhenProjected,
    getMissionPatchForWorkerStatus,
} from "@/modules/companyNeed/helpers/mission";
import { formatMissionActions } from "@/modules/companyNeed/helpers/missionActions";
// Services
import missionService from "@/modules/companyNeed/services/missionService";
import { useCompanyNeedStore } from "@/modules/companyNeed/store/companyNeedStore";
import { useMissionPatchStore } from "@/modules/companyNeed/store/missionPatchStore";
import {
    InitialTabType,
    MISSION_MAX_WORKERS_REACHED_CODE,
    MISSION_WORKER_REGISTRATION_NOT_COMPLETE_CODE,
    MISSION_WORKER_SCHEDULE_CONFLICT_CODE,
    type MissionBulkWorker,
    type MissionModalData,
    type MissionsDetailModalProps,
    type MissionWorker,
    type WorkersSelectionModal,
} from "@/modules/companyNeed/types/missions";
import { MissionAction, MissionState } from '@/modules/companyNeed/views/details/missions/types';
import { WorkerMissionStatus } from '@/types/mission';

import type { IconObject } from "@/dsComponents/buttons/types";
import type { DropdownGeneratorProps } from "@/dsComponents/Dropdown/types";
import type { TabProps } from "@/dsComponents/Tab/types";
import type { UpdateMissionPayload } from "@/modules/companyNeed/components/modals/createOrder/types";

import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";
import Dropdown from "@/dsComponents/Dropdown/Dropdown.vue";
import DropdownGenerator from "@/dsComponents/Dropdown/DropdownGenerator.vue";
import { useModalStore } from "@/dsComponents/modals/modalStore";
import SideModal2 from "@/dsComponents/modals/SideModal2.vue";
// DS Components
import Tab from "@/dsComponents/Tab/Tab.vue";
import VerticalSeparator from "@/dsComponents/VerticalSeparator.vue";

import MissionLabels from "@/components/Mission/MissionLabels.vue";
import MissionDetailsTab from "@/modules/companyNeed/components/modals/mission/details/MissionDetailsTab.vue";
// Components
import MissionPropositionsTab from "@/modules/companyNeed/components/modals/mission/propositions/MissionPropositionsTab.vue";

const props = withDefaults(defineProps<MissionsDetailModalProps>(), {
    initialTab: InitialTabType.details,
});
const emit = defineEmits<(e: "close") => void>();

const { t } = useI18n();

const modalStore = useModalStore();

const currentTab = ref<InitialTabType>(InitialTabType.details);
const missionData = ref<MissionModalData | null>(null);
const isLoading = ref<boolean>(false);

const companyNeedStore = useCompanyNeedStore();
const missionPatchStore = useMissionPatchStore();
const missionActionHandlers = useMissionActionHandlers();
const { toastError } = useDefaultToast();

const tabGroups = computed<TabProps["tabGroups"]>(() => [
    {
        tabItems: [
            {
                id: InitialTabType.details,
                label: t("modals.missionDetails.tabs.details"),
                isActive: currentTab.value === InitialTabType.details,
                icon: "search",
            },
            {
                id: InitialTabType.propositions,
                label: t("modals.missionDetails.tabs.propositions"),
                isActive: currentTab.value === InitialTabType.propositions,
                icon: "users",
            },
        ],
    },
]);

// Status, name and actions changed optimistically before the query-side caught up
const patchedMission = computed(() => (missionData.value ? missionPatchStore.withPatch(missionData.value) : null));

const missionStatusInfo = computed(() => {
    if (!patchedMission.value) return undefined;
    return missionStatusInfoMap[patchedMission.value.status] as IconObject;
});

const canEditMission = computed(() => {
    return patchedMission.value?.availableActions.includes(MissionAction.EDIT) ?? false;
});

const isMissionStarted = computed(() => {
    if (!missionData.value?.periods.length) return false;
    return Date.now() >= getStartMissionDate(missionData.value.periods);
});

const canAddWorkers = computed(() => {
    if (isMissionStarted.value) return false;
    return patchedMission.value?.availableActions.includes(MissionAction.ADD_WORKERS) ?? false;
});

const confirmedWorkerCount = computed(() => {
    if (!missionData.value) return 0;
    const section = missionData.value.workersSelection.find((ws) => ws.workerStatus === WorkerMissionStatus.CONFIRMED);
    return section?.workers.length ?? 0;
});

const hasPropositionWorkersListed = computed(
    // biome-ignore lint/complexity/useOptionalChain: Init Biome
    () => missionData.value !== null && missionData.value.workersSelection.some((ws) => ws.workers.length > 0)
);

const isMissionAllPositionsFilled = computed(
    () => missionData.value !== null && missionData.value.totalPositions > 0 && confirmedWorkerCount.value === missionData.value.totalPositions
);

const showAddWorkersFooter = computed(
    () =>
        !isLoading.value &&
        missionData.value !== null &&
        currentTab.value === InitialTabType.propositions &&
        hasPropositionWorkersListed.value &&
        !isMissionAllPositionsFilled.value
);

const handleEditFromModal = () => {
    const mission = missionData.value;
    if (!mission) return;

    modalStore.actions.open(ModalName.ManageOrderModal, {
        besoinId: mission.besoinId,
        companyId: mission.companyId,
        jobId: mission.jobId,
        mission: {
            id: mission.id,
            ...toManageOrderMissionForm(mission),
            onUpdated: handleMissionUpdated,
        },
    });
};

// The query-side may still return the previous values right after the command
const handleMissionUpdated = (payload: UpdateMissionPayload) => {
    if (missionData.value) missionData.value = applyUpdatePayloadToMissionDetails(missionData.value, payload);
};

const handleDuplicateFromModal = () => {
    const mission = missionData.value;
    if (!mission) return;

    modalStore.actions.open(ModalName.ManageOrderModal, {
        besoinId: mission.besoinId,
        companyId: mission.companyId,
        jobId: mission.jobId,
        isDuplicate: true,
        mission: {
            ...toManageOrderMissionForm(mission),
        },
    });
};

const dropdownActions = computed<DropdownGeneratorProps>(() => {
    if (!missionData.value) {
        return { actions: [] };
    }

    const filteredActions = (patchedMission.value?.availableActions ?? []).filter(
        (action) =>
            action !== MissionAction.DETAILS &&
            action !== MissionAction.PROPOSITIONS &&
            action !== MissionAction.EDIT &&
            action !== MissionAction.ADD_WORKERS
    );

    const handlers = {
        ...missionActionHandlers,
        onEdit: handleEditFromModal,
        onDuplicate: handleDuplicateFromModal,
    };

    const tableActions = formatMissionActions(missionData.value.id, filteredActions, handlers, t);

    const items = tableActions[0]?.dropdownItems?.(missionData.value) || [];
    return { actions: items };
});

const handleSelectTab = (tabId: InitialTabType) => {
    currentTab.value = tabId;
};

const handleClose = () => {
    emit("close");
};

const handleEditMission = () => handleEditFromModal();

const applyAddedWorkerLocally = (worker: MissionBulkWorker, options?: { bumpMissionsListQuantity?: boolean }) => {
    if (missionData.value && options?.bumpMissionsListQuantity !== false) {
        const knownProposedQuantity =
            missionData.value.workersSelection.find((ws) => ws.workerStatus === WorkerMissionStatus.PROPOSED)?.workers.length ?? 0;
        missionPatchStore.bumpProposed(props.missionId, knownProposedQuantity);
    }
    if (missionData.value) {
        const localCopy: MissionWorker = {
            id: worker.workerId,
            firstname: worker.firstname,
            lastname: worker.lastname,
            phone: worker.phone,
            lastupdate: Date.now(),
            history: [{ status: WorkerMissionStatus.PROPOSED, date: Date.now() }],
            availableStatuses: [],
        };
        const proposedSection = missionData.value.workersSelection.find((ws) => ws.workerStatus === WorkerMissionStatus.PROPOSED);
        if (proposedSection) {
            if (!proposedSection.workers.some((w) => w.id === localCopy.id)) {
                proposedSection.workers.push(localCopy);
            }
        } else {
            missionData.value.workersSelection.push({
                workerStatus: WorkerMissionStatus.PROPOSED,
                workers: [localCopy],
            });
        }
    }
    // The detail store only holds the missions of the company need currently displayed
    if (missionData.value?.besoinId !== companyNeedStore.companyNeedId) return;
    companyNeedStore.applyAddedWorkerToMission(props.missionId, worker, {
        bumpMissionsListQuantity: options?.bumpMissionsListQuantity !== false,
        missionSummary: missionData.value ? { id: missionData.value.id, name: missionData.value.name, status: missionData.value.status } : undefined,
    });
};

const fetchMissionData = async () => {
    isLoading.value = true;
    try {
        missionData.value = await missionService.fetchMissionDetails(props.missionId);
    } catch (error) {
        console.error("Error fetching mission data:", error);
    } finally {
        isLoading.value = false;
    }
};

// Refetching right away could return the workers list without the added workers
const refreshAfterWorkersAdded = async (missionId: string, workerIds: string[]) => {
    if (workerIds.length === 0) return;
    const details = await fetchMissionWhenProjected(missionId, (mission) => {
        const projectedIds = new Set(mission.workersSelection.flatMap((ws) => ws.workers.map((w) => w.id)));
        return workerIds.every((id) => projectedIds.has(id));
    });
    if (details && props.missionId === missionId) missionData.value = details;
};

const handleAddWorkers = () => {
    if (!missionData.value || !canAddWorkers.value) return;
    const missionId = props.missionId;
    const addedWorkerIds: string[] = [];
    modalStore.actions.open(ModalName.CompanyNeedMissionAddWorkerToMissionModal, {
        missionId: props.missionId,
        besoinId: missionData.value.besoinId,
        missionTitle: missionData.value.name,
        excludedWorkerIds: missionData.value.workersSelection.flatMap((ws) => ws.workers.map((w) => w.id)),
        onWorkerAdded: (worker: MissionBulkWorker, options?: { bumpMissionsListQuantity?: boolean }) => {
            addedWorkerIds.push(worker.workerId);
            applyAddedWorkerLocally(worker, options);
        },
        onAllAdditionsDone: () => {
            void refreshAfterWorkersAdded(missionId, addedWorkerIds);
        },
    });
};

const handleOpenWorkerDetail = (workerId: string) => {
    modalStore.actions.open(ModalName.ProfilPreviewModal, {
        profilIds: [workerId],
        startingProfileId: workerId,
        onOpenProfil: () => modalStore.actions.close(ModalName.CompanyNeedMissionDetailsModal),
    });
};

const getMissionWorkerErrorCode = (error: unknown): string | undefined =>
    (error as { response?: { data?: { code?: string } } })?.response?.data?.code;

const isMaxWorkersReachedError = (error: unknown): boolean => getMissionWorkerErrorCode(error) === MISSION_MAX_WORKERS_REACHED_CODE;

const isWorkerRegistrationNotCompleteError = (error: unknown): boolean =>
    getMissionWorkerErrorCode(error) === MISSION_WORKER_REGISTRATION_NOT_COMPLETE_CODE;

const isWorkerScheduleConflictError = (error: unknown): boolean =>
    getMissionWorkerErrorCode(error) === MISSION_WORKER_SCHEDULE_CONFLICT_CODE;

// Moves a worker optimistically between status sections without waiting for the read model to update.
// availableStatuses is reset to [] because the server-side projection hasn't caught up yet;
// the correct values will be restored on the next fetchMissionData.
// The history entry is added as the server would, the confirmed gauge and abort reason read it.
const applyOptimisticWorkerStatusChange = (
    workersSelection: WorkersSelectionModal[],
    workerId: string,
    newStatus: WorkerMissionStatus,
    reason?: string
): WorkersSelectionModal[] => {
    let movedWorker: MissionWorker | undefined;

    // Remove the worker from its current section and drop the section if it becomes empty.
    const withoutWorker = workersSelection
        .map((section) => {
            const workerIndex = section.workers.findIndex((worker) => worker.id === workerId);
            if (workerIndex === -1) return section;
            movedWorker = section.workers[workerIndex];
            return { ...section, workers: section.workers.filter((worker) => worker.id !== workerId) };
        })
        .filter((section) => section.workers.length > 0);

    if (!movedWorker) return workersSelection;

    const now = Date.now();
    const updatedWorker: MissionWorker = {
        ...movedWorker,
        availableStatuses: [],
        lastupdate: now,
        history: [...movedWorker.history, { status: newStatus, date: now, reason }],
    };

    const targetSectionIndex = withoutWorker.findIndex((section) => section.workerStatus === newStatus);

    if (targetSectionIndex !== -1) {
        return withoutWorker.map((section, index) => {
            if (index !== targetSectionIndex) return section;
            return { ...section, workers: [updatedWorker, ...section.workers] };
        });
    }

    // Target section does not exist yet: create it at the end of the list.
    return [...withoutWorker, { workerStatus: newStatus, workers: [updatedWorker] }];
};

const updateWorkerStatusOptimistically = async (workerId: string, newStatus: WorkerMissionStatus, reason?: string) => {
    if (!missionData.value || !patchedMission.value) return;
    const snapshot = missionData.value.workersSelection;
    const nextWorkersSelection = applyOptimisticWorkerStatusChange(snapshot, workerId, newStatus, reason);
    missionData.value.workersSelection = nextWorkersSelection;
    const countConfirmed = (selection: WorkersSelectionModal[]) =>
        selection.find((ws) => ws.workerStatus === WorkerMissionStatus.CONFIRMED)?.workers.length ?? 0;
    const confirmedCount = countConfirmed(nextWorkersSelection);
    const missionPatch = {
        ...getMissionPatchForWorkerStatus({
            missionStatus: patchedMission.value.status,
            newWorkerStatus: newStatus,
            confirmedCount,
            totalPositions: missionData.value.totalPositions,
        }),
        // Keeps the confirmed counter of the missions lists in sync
        ...(confirmedCount !== countConfirmed(snapshot) ? { confirmedWorkers: confirmedCount } : {}),
    };
    const rollbackCount = missionPatch.status ? companyNeedStore.shiftMissionsCountForStatus(props.missionId, missionPatch.status) : undefined;
    const rollbackPatch =
        Object.keys(missionPatch).length > 0 ? missionPatchStore.applyPatch(props.missionId, missionPatch) : undefined;
    // Keeps the propositions counter of the missions lists in sync
    const rollbackSelection = missionPatchStore.applyWorkersSelection(
        props.missionId,
        nextWorkersSelection.map((ws) => ({ workerStatus: ws.workerStatus, quantity: ws.workers.length }))
    );
    const rollbackMissionPatch = () => {
        rollbackSelection();
        rollbackPatch?.();
        rollbackCount?.();
    };
    try {
        await missionService.updateMissionWorkerStatus(props.missionId, workerId, { status: newStatus, reason });
    } catch (error) {
        missionData.value.workersSelection = snapshot;
        rollbackMissionPatch();
        if (isMaxWorkersReachedError(error)) {
            modalStore.actions.open(ModalName.TooMuchWorkerConfirm);
        } else if (isWorkerRegistrationNotCompleteError(error)) {
            toastError(t("modals.missionDetails.errors.workerRegistrationNotComplete.single"));
        } else if (isWorkerScheduleConflictError(error)) {
            toastError(t("modals.missionDetails.errors.workerScheduleConflict"));
        } else {
            toastError();
        }
    }
};

const handleWorkerStatusChange = (workerId: string, newStatus: WorkerMissionStatus) => {
    if (newStatus === WorkerMissionStatus.REJECTED_BY_AGENCY) {
        modalStore.actions.open(ModalName.WorkerAgencyRejectModal, {
            open: true,
            onConfirm: (reason: string) => updateWorkerStatusOptimistically(workerId, newStatus, reason),
            newStatus,
        });
    } else {
        updateWorkerStatusOptimistically(workerId, newStatus);
    }
};

const isExpired = computed(() => missionData.value?.states.includes(MissionState.EXPIRED));
const isSyncWithEvolia = computed(() => missionData.value?.states.includes(MissionState.SYNC_WITH_EVOLIA));
const isExtended = computed(() => missionData.value?.states.includes(MissionState.EXTENDED));

onMounted(() => {
    if (props.open) {
        fetchMissionData();
        currentTab.value = props.initialTab;
    }
});
</script>
<template>
    <SideModal2 :title="patchedMission?.name ?? ''" :icon="missionStatusInfo" :titleBorder="false" :width="480" :open="open" close @close="handleClose">
        <template #title-buttons-group>
            <div v-if="isExpired || isSyncWithEvolia || isExtended" class="mission-labels-container">
                <div class="mission-labels-container-inner">
                    <VerticalSeparator height="16px" />
                </div>
                <MissionLabels :isExpired="isExpired" :isSyncWithEvolia="isSyncWithEvolia" :isExtended="isExtended" />
            </div>
        </template>
        <template #header-actions>
            <Dropdown width="240px" close-on-descendant v-if="dropdownActions.actions.length > 0">
                <template #trigger-zone>
                    <ButtonAlt icon-only="dots" size="sm" type="neutral" />
                </template>
                <template #dropdown-content>
                    <DropdownGenerator v-bind="dropdownActions" />
                </template>
            </Dropdown>
            <ButtonAlt v-if="canEditMission" icon-only="edit" size="sm" type="neutral" @click="handleEditMission" />
        </template>
        <div v-if="isLoading" class="loading-state typo-sm-regular">
            <span>{{ t("global.loading") }}</span>
        </div>

        <div v-else-if="missionData" class="mission-details-modal">
            <div class="tabs-container">
                <Tab :tab-groups="tabGroups" @select:tab="handleSelectTab" />
            </div>

            <MissionDetailsTab v-if="currentTab === InitialTabType.details" :mission-data="missionData" />

            <MissionPropositionsTab
                v-else-if="currentTab === InitialTabType.propositions"
                :workersSelection="missionData.workersSelection"
                :positionQuantity="missionData.totalPositions"
                :canAddWorkers="canAddWorkers"
                @openWorkerDetail="handleOpenWorkerDetail"
                @onWorkerStatusChange="handleWorkerStatusChange"
                @openAddWorkers="handleAddWorkers"
            />
        </div>

        <template v-if="showAddWorkersFooter" #footer>
            <Dropdown mode="hover" :disabled="canAddWorkers" width="fit-content" :styles="{ padding: 'var(--spacing-md, 8px)' }" class="add-workers-footer-wrapper">
                <template #trigger-zone>
                    <ButtonAlt
                        type="neutral"
                        variant="stroke"
                        size="md"
                        full-width
                        left-icon="plus"
                        :disabled="!canAddWorkers"
                        :label="t('modals.missionDetails.propositions.addWorkerToMission.button')"
                        @click.stop="handleAddWorkers"
                    />
                </template>
                <template #dropdown-content>
                    <span class="typo-xs-regular disabled-tooltip-text">
                        {{ t("modals.missionDetails.propositions.addWorkerToMission.disabledTooltip") }}
                    </span>
                </template>
            </Dropdown>
        </template>
    </SideModal2>
</template>

<style scoped lang="scss">
.loading-state {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--spacing-6xl, 48px);
    color: var(--text-sub-600, #4c505e);
}

.mission-labels-container {
    display: flex;
    align-items: center;

    .mission-labels-container-inner {
        padding: 0 var(--spacing-md, 8px);
    }
}

.mission-details-modal {
    display: flex;
    flex-direction: column;
    height: 100%;

    .tabs-container {
        position: sticky;
        top: 0;
        z-index: 10;
        background-color: var(--bg-white-0, #ffffff);
    }
}

.add-workers-footer-wrapper {
    width: 100%;
}

.disabled-tooltip-text {
    color: var(--text-sub-600, #4c505e);
    white-space: nowrap;
}
</style>

<style scoped lang="scss">
:deep(.ds-side-modal-component .contenair-side-modal:has(.mission-details-modal)) {
    padding-right: 0;
}
</style>
